import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tab,
  Tabs,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`newsletter-ad-tabpanel-${index}`}
      aria-labelledby={`newsletter-ad-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface NewsletterAdsDetailModalProps {
  open: boolean;
  onClose: () => void;
  adHistoryId: number;
}

const NewsletterAdsDetailModal: React.FC<NewsletterAdsDetailModalProps> = ({
  open,
  onClose,
  adHistoryId,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tabValue, setTabValue] = useState(0);
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && adHistoryId) {
      loadDetails();
    }
  }, [open, adHistoryId]);

  const loadDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchData(
        `/api/newsletter-ads-timeline/details/${adHistoryId}`,
        {},
        false,
      );
      setDetails(data);
    } catch (err: any) {
      console.error('Error loading ad details:', err);
      setError(err.message || 'Failed to load ad details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE');
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return '-';
    return numValue.toLocaleString('de-DE');
  };

  const formatRate = (rate: number | null | undefined) => {
    if (rate === null || rate === undefined) return '-';
    const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (isNaN(numRate)) return '-';
    return `${numRate.toFixed(2)}%`;
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '-';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '-';
    return `€${numAmount.toFixed(2)}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Newsletter Ad Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : details ? (
          <>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="fullWidth"
            >
              <Tab label="Overview" />
              <Tab label="Performance" />
              <Tab label="Details" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Subject
                  </Typography>
                  <Typography variant="body1">{details.subject || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Newsletter
                  </Typography>
                  <Typography variant="body1">{details.newsletter_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Send Date
                  </Typography>
                  <Typography variant="body1">{formatDate(details.send_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Ad Control Code
                  </Typography>
                  <Chip
                    label={details.ad_control_code || '0'}
                    color={details.ad_control_code === 0 ? 'primary' : 'default'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    ESP
                  </Typography>
                  <Chip
                    label={details.it_campaign_id ? 'Iterable' : 'Mailchimp'}
                    color={details.it_campaign_id ? 'secondary' : 'primary'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Campaign ID
                  </Typography>
                  <Typography variant="body1">
                    {details.it_campaign_id || details.campaign_id || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Ad ID
                  </Typography>
                  <Typography variant="body1">{details.ad_id || '-'}</Typography>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Delivery Metrics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Recipients
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(
                      details.nas_recipients || details.mc_recipients || details.it_recipients,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Opens
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(
                      details.nas_opens || details.mc_unique_opens || details.it_unique_opens,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Clicks
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(
                      details.unique_clicks ||
                        details.nas_clicks ||
                        details.mc_unique_clicks ||
                        details.it_unique_clicks,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Leads
                  </Typography>
                  <Typography variant="h6">
                    {formatNumber(details.leads || details.nas_leads)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Performance Rates
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Open Rate
                  </Typography>
                  <Typography variant="h6">
                    {formatRate(
                      details.mc_open_rate * 100 ||
                        (details.nas_opens / details.nas_recipients) * 100,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Click Rate
                  </Typography>
                  <Typography variant="h6">
                    {formatRate(
                      details.mc_click_rate * 100 ||
                        (details.nas_clicks /
                          (details.nas_opens ||
                            details.mc_unique_opens ||
                            details.it_unique_opens ||
                            1)) *
                          100,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Lead Rate
                  </Typography>
                  <Typography variant="h6">
                    {formatRate(
                      ((details.leads || details.nas_leads || 0) /
                        (details.nas_opens ||
                          details.mc_unique_opens ||
                          details.it_unique_opens ||
                          1)) *
                        100,
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    ETKO
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(
                      ((details.turnover || details.nas_turnover || 0) /
                        (details.nas_opens ||
                          details.mc_unique_opens ||
                          details.it_unique_opens ||
                          1)) *
                        1000,
                    )}
                  </Typography>
                </Grid>

                {details.soi_count && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Lead Quality
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        SOI Count
                      </Typography>
                      <Typography variant="h6">{formatNumber(details.soi_count)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        DOI Count
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(details.leads || details.nas_leads)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        DOI Rate
                      </Typography>
                      <Typography variant="h6">
                        {formatRate(
                          ((details.leads || details.nas_leads || 0) / (details.soi_count || 1)) *
                            100,
                        )}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Product Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Product ID
                  </Typography>
                  <Typography variant="body1">{details.product_id || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Product Name
                  </Typography>
                  <Typography variant="body1">{details.product_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Customer
                  </Typography>
                  <Typography variant="body1">
                    {details.product_customer || details.customer_id || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Product Type
                  </Typography>
                  <Typography variant="body1">{details.product_type || '-'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Ad Material
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Form
                  </Typography>
                  <Typography variant="body1">{details.form || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    From Name
                  </Typography>
                  <Typography variant="body1">{details.from_name || '-'}</Typography>
                </Grid>
                {details.preview_text && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Preview Text
                    </Typography>
                    <Typography variant="body1">{details.preview_text}</Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Revenue
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Revenue
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(details.turnover || details.nas_turnover)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Revenue per Lead
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(
                      (details.turnover || details.nas_turnover || 0) /
                        (details.leads || details.nas_leads || 1),
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterAdsDetailModal;
